import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { getDiallerLookupApiCall } from "../../helpers/fakebackend_helper";
import { normalisePhone, useTwilioCall } from "./TwilioCallProvider";
import TwilioAudioSettings from "./TwilioAudioSettings";
import "./TwilioCallDrawer.scss";

const keypadButtons = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "+",
  "0",
  "⌫",
];

const SEARCH_DEBOUNCE_MS = 350;
const SEARCH_MIN_CHARS = 2;

const formatDuration = (seconds = 0) => {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  const mins = Math.floor(safeSeconds / 60);
  const secs = safeSeconds % 60;

  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const buildCustomerRoute = (meta = {}) => {
  const customerId = meta?.customerId;

  if (!customerId) return null;

  return `/customer-single/${customerId}/view`;
};

/* Two initials for the avatar, so a result row is scannable at a glance. */
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

/**
 * The API returns ONE ROW PER DIALABLE NUMBER, so a person with three numbers
 * arrives as three rows. Fold them back into one entry per person, keeping every
 * number, so the list reads as people rather than as a phone book.
 */
const groupDiallerRows = (rows = []) => {
  const byKey = new Map();

  rows.forEach((row) => {
    const isContact = row?.ZGroup === "contactperson";

    const id = isContact ? row?.ContactPerson_Id : row?.Customer_Id;
    if (!id) return;

    const key = `${isContact ? "cp" : "cu"}-${id}`;

    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        id: String(id),
        type: isContact ? "ContactPerson" : "Customer",
        typeLabel: isContact ? "Contact" : "Customer",
        name:
          (isContact ? row?.ContactPerson_FullName : row?.Customer_FullName) ||
          "Unnamed",
        // A contact person hangs off a customer; a customer has none above it.
        company: isContact ? row?.Customer_FullName || "" : "",
        customerId: row?.Customer_Id ? String(row.Customer_Id) : "",
        url: row?.Url || "",
        phones: [],
      });
    }

    const entry = byKey.get(key);
    const number = normalisePhone(row?.TelephoneNumber || "");

    if (!number) return;
    if (entry.phones.some((phone) => phone.number === number)) return;

    entry.phones.push({
      key: `${key}-${row?.TelephoneNumber_Id || number}`,
      number,
      raw: row?.TelephoneNumber || "",
      label: row?.TelephoneNotes || "",
      isDefault: String(row?.FlagDefault) === "1",
    });
  });

  // Default number first so the obvious one is preselected.
  return Array.from(byKey.values()).map((entry) => ({
    ...entry,
    phones: [...entry.phones].sort(
      (a, b) => Number(b.isDefault) - Number(a.isDefault)
    ),
  }));
};

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    className="twilio-call-drawer__section-label"
  >
    {children}
  </Typography>
);

const DetailLine = ({ label, value }) => {
  if (!value) return null;

  return (
    <Typography variant="caption" color="text.secondary" display="block">
      {label}: {value}
    </Typography>
  );
};

const TwilioCallDrawer = ({
  open,
  onClose,
  initialPhone = "",
  initialPerson = null,
}) => {
  const history = useHistory();
  const phoneInputRef = useRef(null);

  const {
    CALL_PHASE,
    identity,
    canUseTwilio,
    callPhase,
    callStatus,
    tokenLoading,
    incomingCaller,
    incomingMeta,
    isDeviceBusy = false,
    isMuted = false,
    durationSeconds = 0,
    startOutboundCall,
    acceptIncomingCall,
    rejectIncomingCall,
    endCall,
    toggleMute = () => {},
  } = useTwilioCall();

  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(initialPerson);
  const [selectedPhone, setSelectedPhone] = useState(
    normalisePhone(initialPhone || "")
  );

  const isIncoming = callPhase === CALL_PHASE.INCOMING;
  const isConnected = callPhase === CALL_PHASE.CONNECTED;
  const isDialling = callPhase === CALL_PHASE.DIALLING;
  const isRegistering = callPhase === CALL_PHASE.REGISTERING;
  const isGettingToken = callPhase === CALL_PHASE.GETTING_TOKEN;
  const isFailed = callPhase === CALL_PHASE.FAILED;

  const hasActiveOrIncomingCall = isIncoming || isConnected;

  // isDeviceBusy keeps the dialler locked when the phase has gone FAILED but the
  // SDK is still on a call — dialling again there is what produced "A Call is
  // already active" while the recipient's phone was still ringing.
  const isDiallerLocked =
    isIncoming ||
    isConnected ||
    isDialling ||
    isRegistering ||
    isGettingToken ||
    isDeviceBusy;

  const canEditDialler = !isDiallerLocked;

  const isReadyForInbound =
    callPhase === CALL_PHASE.READY || callPhase === CALL_PHASE.CONNECTED;

  const activeDisplayName =
    incomingMeta?.customerName ||
    selectedPerson?.name ||
    incomingCaller ||
    "Unknown caller";

  const activePhone =
    incomingMeta?.phone ||
    incomingMeta?.from ||
    incomingCaller ||
    selectedPhone ||
    "";

  const customerRoute = buildCustomerRoute(incomingMeta);

  /* Blended Customer + ContactPerson lookup, debounced, newest response wins. */
  useEffect(() => {
    const term = search.trim();

    if (term.length < SEARCH_MIN_CHARS) {
      setResults([]);
      setSearching(false);
      setSearchError("");
      return undefined;
    }

    let cancelled = false;

    setSearching(true);
    setSearchError("");

    const timer = setTimeout(async () => {
      try {
        // The term travels in the URL path, so it has to be encoded here —
        // FetchAPI appends it verbatim.
        const response = await getDiallerLookupApiCall({
          id: encodeURIComponent(term),
        });

        if (cancelled) return;

        if (response?.status && response.status !== 200) {
          setResults([]);
          setSearchError(response?.message || "Search failed.");
          return;
        }

        setResults(groupDiallerRows(response?.search_list || []));
      } catch (error) {
        if (cancelled) return;

        console.error("Dialler lookup failed:", error);
        setResults([]);
        setSearchError("Search failed.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  const handleCall = useCallback(() => {
    const phone = normalisePhone(selectedPhone);

    if (!phone || isDiallerLocked || tokenLoading || !canUseTwilio) return;

    setSelectedPhone(phone);

    startOutboundCall({
      phone,
      person: selectedPerson,
    });
  }, [
    canUseTwilio,
    isDiallerLocked,
    selectedPerson,
    selectedPhone,
    startOutboundCall,
    tokenLoading,
  ]);

  const handleAnswerAndOpenCustomer = () => {
    acceptIncomingCall({
      openCustomer: true,
      onOpenCustomer: () => {
        if (customerRoute) history.push(customerRoute);
      },
    });
  };

  const handleOpenCustomer = () => {
    if (customerRoute) history.push(customerRoute);
  };

  const handleSelectPerson = (person) => {
    if (!canEditDialler) return;

    setSelectedPerson(person);
    setSelectedPhone(normalisePhone(person?.phones?.[0]?.number || ""));

    setTimeout(() => phoneInputRef.current?.focus(), 100);
  };

  const handleKeypadPress = (value) => {
    if (!canEditDialler) return;

    if (value === "⌫") {
      setSelectedPhone((prev) => prev.slice(0, -1));
      return;
    }

    setSelectedPhone((prev) => normalisePhone(`${prev}${value}`));
  };

  const handleClearSelection = () => {
    if (!canEditDialler) return;

    setSelectedPerson(null);
    setSelectedPhone("");
  };

  useEffect(() => {
    if (!open) return;

    setSelectedPhone(normalisePhone(initialPhone || ""));
    setSelectedPerson(initialPerson || null);

    setTimeout(() => phoneInputRef.current?.focus(), 150);
  }, [open, initialPhone, initialPerson]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyboardInput = (event) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();

      const isTypingInField =
        activeTag === "input" ||
        activeTag === "textarea" ||
        document.activeElement?.isContentEditable;

      if (event.key === "Escape") {
        event.preventDefault();
        onClose?.();
        return;
      }

      if (event.key === "Enter") {
        // A focused button already gets a synthesised click from Enter. Acting
        // here too dialled twice on one keypress, and the second connect came
        // back "A Call is already active" while the first was still ringing.
        if (activeTag === "button" || activeTag === "a") return;

        event.preventDefault();

        if (isIncoming) {
          acceptIncomingCall();
          return;
        }

        handleCall();
        return;
      }

      if (isTypingInField || !canEditDialler) return;

      if (/^[0-9+]$/.test(event.key)) {
        event.preventDefault();
        setSelectedPhone((prev) => normalisePhone(`${prev}${event.key}`));
      }

      if (event.key === "Backspace") {
        event.preventDefault();
        setSelectedPhone((prev) => prev.slice(0, -1));
      }
    };

    window.addEventListener("keydown", handleKeyboardInput);

    return () => window.removeEventListener("keydown", handleKeyboardInput);
  }, [
    open,
    onClose,
    canEditDialler,
    isIncoming,
    acceptIncomingCall,
    handleCall,
  ]);

  const callButtonLabel = useMemo(() => {
    if (isGettingToken) return "Preparing...";
    if (isRegistering) return "Registering...";
    if (isDialling) return "Ringing...";
    if (isConnected) return "Connected";

    return "Call";
  }, [isConnected, isDialling, isGettingToken, isRegistering]);

  // Never leave a live call with no way to hang it up, whatever the phase says.
  const showEndButton =
    isGettingToken ||
    isRegistering ||
    isDialling ||
    isIncoming ||
    isConnected ||
    isDeviceBusy;

  const searchHasTerm = search.trim().length >= SEARCH_MIN_CHARS;

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      PaperProps={{ className: "twilio-call-drawer-paper" }}
    >
      <Box className="twilio-call-drawer">
        <Box className="twilio-call-drawer__header">
          <Box>
            <Typography variant="h6" className="twilio-call-drawer__title">
              Phone
            </Typography>

            <Typography
              variant="caption"
              className="twilio-call-drawer__subtitle"
            >
              {canUseTwilio
                ? `${identity || "Not registered"}${
                    isReadyForInbound ? " · ready" : ""
                  }`
                : "Calling is not enabled for this user"}
            </Typography>
          </Box>

          <IconButton
            className="twilio-call-drawer__header-close"
            onClick={onClose}
            aria-label="Close call drawer"
          >
            <i className="fa-solid fa-xmark" />
          </IconButton>
        </Box>

        <Box className="twilio-call-drawer__content">
          {hasActiveOrIncomingCall && (
            <Paper variant="outlined" className="twilio-call-drawer__incoming">
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {isIncoming ? "Incoming call" : "Active call"}
                  </Typography>

                  <Typography variant="subtitle2" fontWeight={800}>
                    {activeDisplayName}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  {isConnected && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={formatDuration(durationSeconds)}
                    />
                  )}

                  <Chip
                    size="small"
                    color={isConnected ? "success" : "warning"}
                    label={isConnected ? "Connected" : "Ringing"}
                  />
                </Stack>
              </Stack>

              <Box mt={1}>
                <DetailLine label="Phone" value={activePhone} />
                <DetailLine
                  label="Email"
                  value={incomingMeta?.customerData?.Email}
                />
                <DetailLine
                  label="Business"
                  value={incomingMeta?.customerData?.BusinessName}
                />
              </Box>

              {isIncoming && (
                <Stack spacing={1} mt={1.5}>
                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      onClick={() => acceptIncomingCall()}
                      startIcon={<i className="fa-solid fa-phone" />}
                    >
                      Answer
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={rejectIncomingCall}
                      startIcon={<i className="fa-solid fa-phone-slash" />}
                    >
                      Decline
                    </Button>
                  </Stack>

                  {customerRoute && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={handleAnswerAndOpenCustomer}
                      startIcon={<i className="fa-solid fa-user" />}
                    >
                      Answer + open customer
                    </Button>
                  )}
                </Stack>
              )}

              {isConnected && (
                <Stack direction="row" spacing={1} mt={1.5}>
                  <Button
                    fullWidth
                    variant="outlined"
                    color={isMuted ? "warning" : "primary"}
                    onClick={toggleMute}
                    startIcon={
                      <i
                        className={
                          isMuted
                            ? "fa-solid fa-microphone-slash"
                            : "fa-solid fa-microphone"
                        }
                      />
                    }
                  >
                    {isMuted ? "Unmute" : "Mute"}
                  </Button>

                  {customerRoute && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="primary"
                      onClick={handleOpenCustomer}
                      startIcon={<i className="fa-solid fa-user" />}
                    >
                      Customer
                    </Button>
                  )}
                </Stack>
              )}
            </Paper>
          )}

          <Box>
            <SectionLabel>Who are you calling?</SectionLabel>

            <TextField
              fullWidth
              size="small"
              margin="dense"
              placeholder="Search customers and contacts, or a number"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              disabled={!canEditDialler}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i className="fa-solid fa-magnifying-glass" />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} />
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {searchHasTerm && (
            <Paper variant="outlined" className="twilio-call-drawer__results">
              <List dense disablePadding>
                {results.map((person) => (
                  <ListItemButton
                    key={person.key}
                    selected={selectedPerson?.key === person.key}
                    disabled={!canEditDialler}
                    onClick={() => handleSelectPerson(person)}
                    alignItems="flex-start"
                  >
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="flex-start"
                      width="100%"
                    >
                      <Box className="twilio-call-drawer__result-avatar">
                        {getInitials(person.name)}
                      </Box>

                      <Box minWidth={0} flex={1}>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <Typography variant="body2" fontWeight={700} noWrap>
                            {person.name}
                          </Typography>

                          <Chip
                            size="small"
                            variant="outlined"
                            label={person.typeLabel}
                          />
                        </Stack>

                        {person.company && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            noWrap
                          >
                            {person.company}
                          </Typography>
                        )}

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          noWrap
                        >
                          {person.phones[0]?.number || "No number"}
                          {person.phones.length > 1
                            ? ` +${person.phones.length - 1} more`
                            : ""}
                        </Typography>
                      </Box>
                    </Stack>
                  </ListItemButton>
                ))}

                {!results.length && (
                  <Box p={2}>
                    <Typography variant="body2" color="text.secondary">
                      {searching
                        ? "Searching..."
                        : searchError ||
                          "No customers or contacts match that."}
                    </Typography>
                  </Box>
                )}
              </List>
            </Paper>
          )}

          {selectedPerson && (
            <Paper variant="outlined" className="twilio-call-drawer__card">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Box minWidth={0}>
                  <Typography variant="subtitle2" fontWeight={800} noWrap>
                    {selectedPerson.name}
                  </Typography>

                  {selectedPerson.company && (
                    <Typography variant="caption" color="text.secondary">
                      {selectedPerson.company}
                    </Typography>
                  )}
                </Box>

                <IconButton
                  size="small"
                  onClick={handleClearSelection}
                  disabled={!canEditDialler}
                  aria-label="Clear selected person"
                >
                  <i className="fa-solid fa-xmark" />
                </IconButton>
              </Stack>

              {selectedPerson.phones?.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" mt={1} useFlexGap>
                  {selectedPerson.phones.map((phone) => {
                    const isSelected = selectedPhone === phone.number;

                    return (
                      <Chip
                        key={phone.key}
                        size="small"
                        label={
                          phone.label
                            ? `${phone.label}: ${phone.number}`
                            : phone.number
                        }
                        onClick={() => {
                          if (canEditDialler) setSelectedPhone(phone.number);
                        }}
                        color={isSelected ? "primary" : "default"}
                        variant={isSelected ? "filled" : "outlined"}
                      />
                    );
                  })}
                </Stack>
              )}
            </Paper>
          )}

          <Box>
            <SectionLabel>Number</SectionLabel>

            <TextField
              fullWidth
              size="small"
              margin="dense"
              inputRef={phoneInputRef}
              value={selectedPhone}
              disabled={!canEditDialler}
              onChange={(event) =>
                setSelectedPhone(normalisePhone(event.target.value))
              }
              placeholder="+447700900123"
              helperText="Enter to call, Esc to close."
            />
          </Box>

          <Box className="twilio-call-drawer__keypad">
            {keypadButtons.map((button) => (
              <Button
                key={button}
                variant="outlined"
                className="twilio-call-drawer__keypad-button"
                disabled={!canEditDialler}
                onClick={() => handleKeypadPress(button)}
              >
                {button}
              </Button>
            ))}
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="success"
              onClick={handleCall}
              disabled={
                !canEditDialler ||
                tokenLoading ||
                !canUseTwilio ||
                !selectedPhone
              }
              startIcon={<i className="fa-solid fa-phone" />}
            >
              {callButtonLabel}
            </Button>

            <Button
              size="large"
              variant="outlined"
              color="error"
              onClick={endCall}
              disabled={!showEndButton}
              startIcon={<i className="fa-solid fa-phone-slash" />}
            >
              End
            </Button>
          </Stack>

          <Paper
            variant="outlined"
            className={`twilio-call-drawer__status ${
              isDiallerLocked ? "twilio-call-drawer__status--active" : ""
            } ${isFailed ? "twilio-call-drawer__status--error" : ""} ${
              isConnected ? "twilio-call-drawer__status--connected" : ""
            } ${isIncoming ? "twilio-call-drawer__status--incoming" : ""}`}
          >
            <Typography variant="caption" color="text.secondary">
              Call status
            </Typography>

            <Typography variant="body2" fontWeight={700}>
              {callStatus}
            </Typography>
          </Paper>

          <TwilioAudioSettings />
        </Box>
      </Box>
    </Drawer>
  );
};

export default TwilioCallDrawer;
